import React, { useState, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { Client } from "@microsoft/microsoft-graph-client";
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Stack,
  Alert,
  AlertTitle,
  Chip,
  Tooltip,
  ListItemAvatar,
  Avatar
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SecurityIcon from "@mui/icons-material/Security";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

/** Microsoft Graph well-known appId */
const MS_GRAPH_APP_ID = "00000003-0000-0000-c000-000000000000";

/** A single scope/role required by your app. */
interface ResourceAccess {
  id: string;
  type: "Scope" | "Role";
  friendlyName?: string;
}

/** The top-level structure describing a resource your app calls. */
interface RequiredResourceAccess {
  resourceAppId: string;
  resourceAccess: ResourceAccess[];
}

/**
 * Extend RequiredResourceAccess to include the new `resourceName` property
 */
interface ExtendedRequiredResourceAccess extends RequiredResourceAccess {
  resourceName?: string;
}

/** Minimal shape for friendly name resolution. */
interface ServicePrincipalData {
  appRoles: {
    id: string;
    displayName?: string;
    value?: string;
  }[];
  oauth2PermissionScopes: {
    id: string;
    displayName?: string;
    value?: string;
  }[];
  appDisplayName?: string;
  displayName?: string;
}

/** Props for our read-only manager. */
interface ApiPermissionsManagerProps {
  appId: string;
  clientAppId?: string;
  clientServicePrincipalId: string;
  initialRequiredResourceAccess: RequiredResourceAccess[];
}

/**
 * A read-only API permissions viewer that displays current permissions and consent status
 */
const ApiPermissionsManager: React.FC<ApiPermissionsManagerProps> = ({
  appId,
  clientAppId,
  clientServicePrincipalId,
  initialRequiredResourceAccess,
}) => {
  const { instance, accounts } = useMsal();

  // The local array of permissions your app requires (read-only)
  const [requiredResourceAccess] = useState<RequiredResourceAccess[]>(
    initialRequiredResourceAccess
  );
  
  // A version with friendly names (from resource SP)
  const [resolvedRequiredResourceAccess, setResolvedRequiredResourceAccess] = 
    useState<ExtendedRequiredResourceAccess[]>([]);

  // Track admin consent per resource
  const [adminConsentMap, setAdminConsentMap] = useState<Record<string, boolean>>({});

  // Loading spinner
  const [loading, setLoading] = useState(false);

  // State to control expansion
  const [apiPermissionsExpanded, setApiPermissionsExpanded] = useState(true);

  /** Acquire Graph token with Directory.Read.All */
  const getGraphToken = async (): Promise<string> => {
    const request = { scopes: ["Directory.Read.All"], account: accounts[0] };
    const response = await instance.acquireTokenSilent(request);
    return response.accessToken;
  };

  // On mount => resolve names + admin consent
  useEffect(() => {
    void resolveFriendlyNames(requiredResourceAccess);
    void refreshAdminConsentStatus(requiredResourceAccess);
  }, [requiredResourceAccess]);

  /**
   * Resolves friendly names for each resource access. 
   * Also adds a `resourceName` field with a friendly name for the overall resource.
   */
  const resolveFriendlyNames = async (rraList: RequiredResourceAccess[]) => {
    try {
      const token = await getGraphToken();
      const client = Client.init({
        authProvider: (done) => done(null, token),
      });

      const updated: ExtendedRequiredResourceAccess[] = [];

      for (const resource of rraList) {
        let spData: ServicePrincipalData | null = null;
        try {
          const spResp = await client
            .api(`/servicePrincipals?$filter=appId eq '${resource.resourceAppId}'`)
            .get();
          if (spResp.value && spResp.value.length > 0) {
            spData = spResp.value[0];
          }
        } catch (err) {
          console.warn(`SP not found for resourceAppId=${resource.resourceAppId}`, err);
        }

        // Fallback if not found:
        if (!spData) {
          updated.push({
            ...resource,
            resourceName: resource.resourceAppId, // fallback
          });
          continue;
        }

        // Build friendly overall resourceName
        const displayName =
          spData.appDisplayName ||
          spData.displayName ||
          `Resource ${resource.resourceAppId}`;

        // Build maps for role/scope display names
        const rolesMap = new Map(
          (spData.appRoles || []).map((r) => [
            r.id,
            r.displayName || r.value || "Unnamed Role",
          ])
        );
        const scopesMap = new Map(
          (spData.oauth2PermissionScopes || []).map((s) => [
            s.id,
            s.value || s.displayName || "Unnamed Scope",
          ])
        );

        // Add friendly names inside each resourceAccess item
        const newRA = resource.resourceAccess.map((ra) => {
          if (ra.type === "Scope" && scopesMap.has(ra.id)) {
            return { ...ra, friendlyName: scopesMap.get(ra.id) };
          } else if (ra.type === "Role" && rolesMap.has(ra.id)) {
            return { ...ra, friendlyName: rolesMap.get(ra.id) };
          }
          return ra;
        });

        updated.push({
          ...resource,
          resourceName: displayName,
          resourceAccess: newRA,
        });
      }

      setResolvedRequiredResourceAccess(updated);
    } catch (err) {
      console.error("Error in resolveFriendlyNames:", err);
      // fallback to original if resolution fails
      setResolvedRequiredResourceAccess(
        rraList.map((rra) => ({ ...rra, resourceName: rra.resourceAppId }))
      );
    }
  };

  // Admin Consent Check
  const refreshAdminConsentStatus = async (rraList: RequiredResourceAccess[]) => {
    setLoading(true);
    try {
      const token = await getGraphToken();
      const client = Client.init({
        authProvider: (done) => done(null, token),
      });

      const newMap: Record<string, boolean> = {};
      for (const resource of rraList) {
        const spResp = await client
          .api(`/servicePrincipals?$filter=appId eq '${resource.resourceAppId}'`)
          .get();
        if (!spResp.value?.length) {
          newMap[resource.resourceAppId] = false;
          continue;
        }
        const resourceSpId = spResp.value[0].id;

        // If any tenant-wide grant => admin consent is done
        const grantsResp = await client
          .api(
            `/oauth2PermissionGrants?$filter=clientId eq '${clientServicePrincipalId}' AND resourceId eq '${resourceSpId}' AND consentType eq 'AllPrincipals'`
          )
          .get();
        newMap[resource.resourceAppId] = (grantsResp.value?.length || 0) > 0;
      }

      setAdminConsentMap(newMap);
    } catch (err) {
      console.error("Error refreshing admin consent:", err);
    } finally {
      setLoading(false);
    }
  };

  // Separate Graph from "other" resources
  const graphResources = resolvedRequiredResourceAccess.filter(
    (r) => r.resourceAppId === MS_GRAPH_APP_ID
  );
  const customResources = resolvedRequiredResourceAccess.filter(
    (r) => r.resourceAppId !== MS_GRAPH_APP_ID
  );

  // Enhanced resource list renderer
  function renderResourceList(resources: ExtendedRequiredResourceAccess[]) {
    if (resources.length === 0) {
      return (
        <Box sx={{ 
          p: 4, 
          m: 2,
          backgroundColor: '#f8f9fa', 
          borderRadius: '12px', 
          textAlign: 'center',
          border: '2px dashed #dee2e6'
        }}>
          <Box sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            backgroundColor: '#e9ecef',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2
          }}>
            <SecurityIcon sx={{ fontSize: '2rem', color: '#6c757d' }} />
          </Box>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            No API permissions configured
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This application does not have any API permissions configured.
          </Typography>
        </Box>
      );
    }

    return resources.map((rra) => {
      const isGranted = adminConsentMap[rra.resourceAppId] === true;

      return (
        <Box key={rra.resourceAppId} sx={{ mb: 3 }}>
          <Box sx={{ 
            p: 3, 
            backgroundColor: '#ffffff', 
            border: '1px solid #e8eaf6',
            borderRadius: '12px',
            mb: 1,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontSize: '1.1rem', fontWeight: 600, mb: 0.5 }}>
                  {rra.resourceName ?? rra.resourceAppId}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Resource AppId: {rra.resourceAppId}
                </Typography>
                <Chip 
                  size="small"
                  label={isGranted ? "Admin Consent Granted ✓" : "Admin Consent Required ✗"}
                  sx={{ 
                    backgroundColor: isGranted ? '#e8f5e9' : '#ffebee',
                    color: isGranted ? '#2e7d32' : '#d32f2f',
                    fontWeight: 500,
                    border: `1px solid ${isGranted ? '#c8e6c9' : '#ffcdd2'}`
                  }}
                />
              </Box>
            </Box>

            <List sx={{ 
              bgcolor: "#fafbff", 
              border: "1px solid #e8eaf6",
              borderRadius: '8px',
              overflow: 'hidden'
            }}>
              {rra.resourceAccess.map((ra, index) => (
                <ListItem
                  key={ra.id}
                  sx={{
                    borderBottom: index < rra.resourceAccess.length - 1 ? "1px solid #f0f0f0" : "none",
                    '&:hover': { backgroundColor: '#f5f5ff' },
                    py: 1.5
                  }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ 
                      bgcolor: ra.type === "Role" ? '#f3e5f5' : '#e3f2fd',
                      width: 32,
                      height: 32
                    }}>
                      {ra.type === "Role" 
                        ? <SecurityIcon sx={{ fontSize: '1rem', color: '#7b1fa2' }} />
                        : <CheckCircleOutlineIcon sx={{ fontSize: '1rem', color: '#1976d2' }} />
                      }
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight="medium" sx={{ mr: 1 }}>
                          {ra.friendlyName || ra.id}
                        </Typography>
                        <Chip 
                          size="small" 
                          label={ra.type} 
                          color={ra.type === "Role" ? "secondary" : "primary"}
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 0.5 }}>
                        <Typography variant="caption" display="block" color="text.secondary">
                          <strong>Permission ID:</strong> {ra.id}
                        </Typography>
                        {ra.friendlyName && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            <strong>Permission Value:</strong> {ra.friendlyName}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </Box>
      );
    });
  }

  const totalPermissions = (graphResources.length > 0 ? graphResources[0]?.resourceAccess.length || 0 : 0) + 
                          customResources.reduce((acc, res) => acc + res.resourceAccess.length, 0);

  return (
    <Card variant="outlined" sx={{ mb: 4 }}>
      <CardContent>
        <Accordion 
          expanded={apiPermissionsExpanded} 
          onChange={() => setApiPermissionsExpanded(!apiPermissionsExpanded)}
          sx={{
            border: '1px solid #e0e7ff',
            borderRadius: '8px !important',
            '&:before': { display: 'none' },
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            mb: 2
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              background: 'linear-gradient(135deg, #e8eaf6 0%, #f3e5f5 100%)',
              borderBottom: apiPermissionsExpanded ? '1px solid #e0e7ff' : 'none',
              minHeight: '64px'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <Box sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: '#3f51b5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2
              }}>
                <SecurityIcon sx={{ color: 'white', fontSize: '1.2rem' }} />
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                  fontSize: "1.25rem",
                  fontWeight: 600,
                  color: "#1a237e",
                  letterSpacing: "0.02em",
                  flexGrow: 1
                }}
              >
                API Permissions
              </Typography>
              <Chip
                size="small"
                label={`${totalPermissions} permissions`}
                sx={{
                  mr: 1,
                  backgroundColor: '#e3f2fd',
                  color: '#1565c0',
                  fontWeight: 500,
                  border: '1px solid #bbdefb'
                }}
              />
              {clientAppId && (
                <Tooltip title="Configure in Entra Portal">
                  <IconButton
                    size="small"
                    component="a"
                    href={`https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps/ApplicationMenuBlade/~/CallAnAPI/appId/${clientAppId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    sx={{ ml: 1, color: '#5c6bc0' }}
                  >
                    <OpenInNewIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </AccordionSummary>
          
          <AccordionDetails sx={{ p: 0, backgroundColor: "#fafbff" }}>
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            )}

            {/* Microsoft Graph API Section */}
            {graphResources.length > 0 && (
              <Box sx={{ p: 3, borderBottom: '1px solid #e8eaf6' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '4px',
                    backgroundColor: '#1976d2',
                    mr: 1.5
                  }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1976d2', fontSize: '1.1rem' }}>
                    Microsoft Graph API Permissions
                  </Typography>
                  <Chip 
                    size="small"
                    label="Microsoft Graph"
                    sx={{ 
                      ml: 'auto',
                      backgroundColor: '#e3f2fd',
                      color: '#1976d2'
                    }}
                  />
                </Box>
                {renderResourceList(graphResources)}
              </Box>
            )}

            {/* Custom APIs Section */}
            {customResources.length > 0 && (
              <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '4px',
                    backgroundColor: '#7b1fa2',
                    mr: 1.5
                  }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#7b1fa2', fontSize: '1.1rem' }}>
                    Custom API Permissions
                  </Typography>
                  <Chip 
                    size="small"
                    label={`${customResources.length} APIs`}
                    sx={{ 
                      ml: 'auto',
                      backgroundColor: '#f3e5f5',
                      color: '#7b1fa2'
                    }}
                  />
                </Box>
                {renderResourceList(customResources)}
              </Box>
            )}

            {/* Empty state */}
            {graphResources.length === 0 && customResources.length === 0 && !loading && (
              <Box sx={{ 
                p: 4, 
                m: 2,
                backgroundColor: '#f8f9fa', 
                borderRadius: '12px', 
                textAlign: 'center',
                border: '2px dashed #dee2e6'
              }}>
                <Box sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  backgroundColor: '#e9ecef',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2
                }}>
                  <SecurityIcon sx={{ fontSize: '2rem', color: '#6c757d' }} />
                </Box>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                  No API permissions configured
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This application does not have any API permissions configured.
                </Typography>
              </Box>
            )}

            {/* About API Permissions */}
            <Box sx={{
              m: 2,
              borderRadius: '8px',
              overflow: 'hidden',
              boxShadow: '0 1px 4px rgba(0, 0, 0, 0.04)',
              opacity: 0.85
            }}>
              <Accordion sx={{ '&:before': { display: 'none' }, borderRadius: '8px !important' }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ fontSize: '1.2rem', color: '#9e9e9e' }} />}
                  sx={{
                    background: '#f8f9fa',
                    borderBottom: '1px solid #e9ecef',
                    minHeight: '48px',
                    '&:hover': { backgroundColor: '#f1f3f4' }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <InfoOutlinedIcon sx={{ mr: 1.5, fontSize: "1.1rem", color: 'info.main' }} />
                    <Typography variant="subtitle1" sx={{ fontSize: "1rem", fontWeight: 500 }}>
                      About API Permissions
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ p: 2, backgroundColor: "#f5f5ff", borderRadius: 1 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      API permissions determine what resources your app can access on behalf of a user or itself.
                    </Typography>
                    
                    <Box sx={{ 
                      p: 2, 
                      backgroundColor: '#ffffff', 
                      border: '1px solid #e0e0ff', 
                      borderRadius: 1,
                      mb: 2
                    }}>
                      <Typography variant="subtitle2" sx={{ color: '#0d47a1', mb: 1 }}>
                        Understanding Permission Types:
                      </Typography>
                      <Typography variant="body2">
                        • <strong>Delegated permissions (Scope)</strong>: Used when your app accesses an API with a signed-in user present<br />
                        • <strong>Application permissions (Role)</strong>: Used when your app runs as a background service without a user present<br />
                        • <strong>Custom API permissions</strong>: Used to access other registered applications in your tenant
                      </Typography>
                    </Box>
                    
                    <Box sx={{
                      p: 2,
                      backgroundColor: '#fff8e1',
                      border: '1px solid #ffe082',
                      borderRadius: 1
                    }}>
                      <Typography variant="subtitle2" sx={{ color: '#bf360c', mb: 1 }}>
                        Important Considerations:
                      </Typography>
                      <Typography variant="body2">
                        • Admin consent is required for Application permissions and some sensitive Delegated permissions<br />
                        • "User.Read" is typically required by almost all applications<br />
                        • Each permission increases the scope of your application's access, so follow the principle of least privilege<br />
                        • Custom API permissions require the target API to have defined App Roles or OAuth2 Permission Scopes
                      </Typography>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>
          </AccordionDetails>
        </Accordion>
      </CardContent>
    </Card>
  );
};

export default ApiPermissionsManager;